import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AnimatePresence } from "framer-motion";
import React from "react";

// Context Providers
import { ThemeProvider } from "@/contexts/ThemeContext";
import { VoiceAgentProvider } from "@/contexts/VoiceAgentContext";
import { AutomationProvider } from "@/contexts/AutomationContext";
import { ConversationProvider } from "@elevenlabs/react";

// Components
import SwarmOrchestrator from "@/components/SwarmOrchestrator.jsx";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Login from "@/pages/Login";
import PlatformAdmin from "@/pages/PlatformAdmin";
import CanonicalRuns from "@/pages/CanonicalRuns";
import AppShell from "@/pages/AppShell";
import OptimizationRunViewer from "@/pages/OptimizationRunViewer";
import HavenDeal from "@/pages/HavenDeal";
const EnterpriseRunsHub = React.lazy(() => import("@/pages/EnterpriseRunsHub"));
import EnterpriseConsolePage from "@/pages/EnterpriseConsolePage";
import EmbedApp from "@/pages/EmbedApp";
import SwarmConstellation from "@/pages/SwarmConstellation";
import DealAnalysis from "@/pages/DealAnalysis";
import Settings from "@/pages/Settings";
import PricingPage from "@/pages/PricingPage";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import TeamJoin from "@/pages/TeamJoin";
import SharedWorkflow from "@/pages/SharedWorkflow";
import CompanyPage from "@/pages/CompanyPage";
import BlogPage from "@/pages/BlogPage";
import CareersPage from "@/pages/CareersPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SecurityPage from "@/pages/SecurityPage";
import StatusPage from "@/pages/StatusPage";
import Onboarding from "@/pages/Onboarding";
import VistaDemo from "@/pages/VistaDemo";
import CookieConsent from "@/components/CookieConsent";

// Heavy demo page — lazy-loaded so it doesn't bloat the landing/dashboard bundle
const Portfolio = React.lazy(() => import("@/pages/Portfolio"));
const ProviderRates = React.lazy(() => import("@/pages/ProviderRates"));
const SpendProtection = React.lazy(() => import("@/pages/SpendProtection"));
const HavenSwarm = React.lazy(() => import("@/pages/HavenSwarm"));
const HarvestLiveWorkspace = React.lazy(() => import("@/pages/HarvestLiveWorkspace"));
const CshSwarm = React.lazy(() => import("@/pages/CshSwarm"));const PortfolioLab = React.lazy(() => import("@/pages/PortfolioLab"));const HavenMandate = React.lazy(() => import("@/pages/HavenMandate"));const RightslineDashboard = React.lazy(() => import("@/pages/RightslineDashboard"));const InvestorHero = React.lazy(() => import("@/pages/InvestorHero"));const InvestorReel = React.lazy(() => import("@/pages/InvestorReel"));const InvestorExplainer = React.lazy(() => import("@/pages/InvestorExplainer"));const InvestorMetricsPreview = React.lazy(() => import("@/pages/InvestorMetricsPreview"));const InvestorSandbox = React.lazy(() => import("@/pages/InvestorSandbox"));const InvestorSimulate = React.lazy(() => import("@/pages/InvestorSimulate"));const InvestorGallery = React.lazy(() => import("@/pages/InvestorGallery"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Configure axios defaults
axios.defaults.withCredentials = true;

// Global 401 -> refresh-and-retry interceptor. The access_token cookie expires
// after 15 min; without this, any authed call (e.g. create project) fails once
// the token lapses. We refresh once and retry, queueing concurrent 401s.
let _isRefreshing = false;
let _refreshWaiters = [];
const _AUTH_SKIP = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/me", "/auth/logout"];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthEndpoint = _AUTH_SKIP.some((p) => url.includes(p));

    if (status !== 401 || !original || original._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }
    original._retried = true;

    if (_isRefreshing) {
      // Wait for the in-flight refresh, then retry.
      return new Promise((resolve, reject) => {
        _refreshWaiters.push({ resolve, reject, original });
      });
    }

    _isRefreshing = true;
    try {
      await axios.post(`${API}/auth/refresh`);
      _refreshWaiters.forEach((w) => w.resolve(axios(w.original)));
      _refreshWaiters = [];
      return axios(original);
    } catch (refreshErr) {
      _refreshWaiters.forEach((w) => w.reject(refreshErr));
      _refreshWaiters = [];
      return Promise.reject(error);
    } finally {
      _isRefreshing = false;
    }
  }
);

// Auth Context with WebSocket support
export const AuthContext = React.createContext(null);

// WebSocket hook for real-time notifications
export const useWebSocket = (sessionToken, onMessage) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    if (!sessionToken) return;

    const connect = () => {
      try {
        wsRef.current = new WebSocket(`${WS_URL}/ws/${sessionToken}`);
        
        wsRef.current.onopen = () => {
          console.log("WebSocket connected");
        };

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            onMessage?.(data);
          } catch (e) {
            console.error("WebSocket message parse error:", e);
          }
        };

        wsRef.current.onclose = () => {
          console.log("WebSocket disconnected");
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        };

        wsRef.current.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
      } catch (e) {
        console.error("WebSocket connection error:", e);
      }
    };

    connect();

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [sessionToken, onMessage]);

  return wsRef;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          if (response.data) {
            toast.success("Welcome to QNNEAL!");
            navigate("/app", { state: { user: response.data }, replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
      <div className="text-[var(--qn-primary)] animate-pulse">Authenticating...</div>
    </div>
  );
};

// WebSocket Bridge - stores WS on window for cross-component presence
const WebSocketBridge = ({ user }) => {
  useEffect(() => {
    if (!user?.session_token) return;
    const ws = new WebSocket(`${WS_URL}/ws/${user.session_token}`);
    ws.onopen = () => { window.__qnneal_ws = ws; };
    ws.onclose = () => { window.__qnneal_ws = null; };
    return () => { ws.close(); window.__qnneal_ws = null; };
  }, [user?.session_token]);
  return null;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const refreshAttempted = useRef(false);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Try token refresh once
        if (!refreshAttempted.current && error.response?.status === 401) {
          refreshAttempted.current = true;
          try {
            await axios.post(`${API}/auth/refresh`);
            const retryRes = await axios.get(`${API}/auth/me`);
            if (retryRes.data) {
              setUser(retryRes.data);
              setIsAuthenticated(true);
              return;
            }
          } catch {}
        }
        setIsAuthenticated(false);
        const returnTo = location.pathname !== "/login" ? `?return_to=${encodeURIComponent(location.pathname + location.search)}` : "";
        navigate(`/login${returnTo}`);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/nav-state auth check only; must not re-run on every pathname/search change
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
          <div className="text-[var(--qn-foreground-muted)]">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const rt = location.pathname !== "/login" ? `?return_to=${encodeURIComponent(location.pathname + location.search)}` : "";
    return <Navigate to={`/login${rt}`} replace />;
  }

  const isDemo = user?.is_demo;

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <WebSocketBridge user={user} />
      {isDemo && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 text-black text-center py-1.5 text-sm font-medium" data-testid="demo-mode-banner">
          DEMO MODE — <button onClick={() => navigate("/login")} className="underline font-bold hover:text-black/80">Sign up for full access</button>
        </div>
      )}
      <div className={isDemo ? "pt-8" : ""}>
        {children}
      </div>
    </AuthContext.Provider>
  );
};

// Platform Admin Route — strict guard: only the env-seeded platform super-admin.
// Non-platform-admins are redirected away; they can never see or load the page.
const PlatformAdminRoute = ({ children }) => {
  const [state, setState] = useState("checking"); // checking | allowed | denied
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (cancelled) return;
        if (data?.is_platform_admin === true) {
          setState("allowed");
        } else {
          setState("denied");
          navigate("/app", { replace: true });
        }
      } catch {
        if (!cancelled) { setState("denied"); navigate("/login", { replace: true }); }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state !== "allowed") return <Navigate to="/app" replace />;
  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/swarm" element={<ProtectedRoute><SwarmConstellation /></ProtectedRoute>} />
        <Route path="/deal-analysis" element={<ProtectedRoute><DealAnalysis /></ProtectedRoute>} />
        <Route path="/embed" element={<EmbedApp />} />
        <Route path="/platform-admin" element={<PlatformAdminRoute><PlatformAdmin /></PlatformAdminRoute>} />
        <Route path="/enterprise" element={<ProtectedRoute><EnterpriseConsolePage /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><ClassicDashboardGate /></ProtectedRoute>} />
        <Route path="/harvest/live" element={<ProtectedRoute><HarvestLiveWorkspace /></ProtectedRoute>} />
        <Route path="/runs" element={<ProtectedRoute><CanonicalRuns /></ProtectedRoute>} />
        <Route path="/runs/:runId" element={<ProtectedRoute><CanonicalRuns /></ProtectedRoute>} />
        <Route path="/enterprise-runs" element={<ProtectedRoute><React.Suspense fallback={null}><EnterpriseRunsHub /></React.Suspense></ProtectedRoute>} />
        <Route path="/enterprise-runs/:tab" element={<ProtectedRoute><React.Suspense fallback={null}><EnterpriseRunsHub /></React.Suspense></ProtectedRoute>} />
        {/* Phase 4 — Universal Tenant Workspace shell (route-derived areas) */}
        <Route path="/app" element={<ProtectedRoute><Navigate to="/app/chat" replace /></ProtectedRoute>} />
        <Route path="/app/optimization/runs/:runId" element={<ProtectedRoute><OptimizationRunViewer /></ProtectedRoute>} />
        <Route path="/app/optimization/haven-deal" element={<ProtectedRoute><HavenDeal /></ProtectedRoute>} />
        <Route path="/app/:area/snapshot/:runId/:seq" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        <Route path="/app/:area/demo/:scenario" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        <Route path="/app/:area" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        <Route path="/app/:area/:sub" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        <Route path="/dashboard/optimize" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/dashboard/savings" element={<ProtectedRoute><Dashboard initialTab="savings" /></ProtectedRoute>} />
        <Route path="/dashboard/models" element={<ProtectedRoute><Dashboard initialTab="models" /></ProtectedRoute>} />
        <Route path="/dashboard/knowledge" element={<ProtectedRoute><Dashboard initialTab="knowledge" /></ProtectedRoute>} />
        <Route path="/dashboard/analytics" element={<ProtectedRoute><Dashboard initialTab="analytics" /></ProtectedRoute>} />
        <Route path="/savings" element={<ProtectedRoute><Dashboard initialTab="savings" /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><Dashboard initialTab="team-mgmt" /></ProtectedRoute>} />
        <Route path="/team-mgmt" element={<ProtectedRoute><Dashboard initialTab="team-mgmt" /></ProtectedRoute>} />
        <Route path="/usage" element={<ProtectedRoute><Dashboard initialTab="usage" /></ProtectedRoute>} />
        <Route path="/models" element={<ProtectedRoute><Dashboard initialTab="models" /></ProtectedRoute>} />
        <Route path="/playground" element={<ProtectedRoute><Dashboard initialTab="playground" /></ProtectedRoute>} />
        <Route path="/knowledge-base" element={<ProtectedRoute><Dashboard initialTab="knowledge" /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Dashboard initialTab="analytics" /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Dashboard initialTab="billing" /></ProtectedRoute>} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/workflow/new" element={<ProtectedRoute><WorkflowBuilder /></ProtectedRoute>} />
        <Route path="/teams/join/:inviteCode" element={<ProtectedRoute><TeamJoin /></ProtectedRoute>} />
        <Route path="/shared/:shareCode" element={<SharedWorkflow />} />
        {/* Public footer pages */}
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/system-status" element={<StatusPage />} />
        <Route path="/dashboard/status" element={<ProtectedRoute><Dashboard initialTab="status" /></ProtectedRoute>} />
        {/* Vista Equity demo — public route, auth happens client-side via key paste */}
        <Route path="/demo/vista" element={<VistaDemo />} />
        {/* Portfolio Inference Cost Simulator — public sales demo, client-side data only */}
        <Route
          path="/portfolio"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
                  <div className="text-zinc-500 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Loading portfolio simulator…
                  </div>
                </div>
              }
            >
              <Portfolio />
            </React.Suspense>
          }
        />
        {/* H&F variant — completely isolated from Vista's /portfolio */}
        <Route
          path="/portfolio/hf"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
                  <div className="text-zinc-500 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Loading portfolio simulator…
                  </div>
                </div>
              }
            >
              <Portfolio firm="hf" />
            </React.Suspense>
          }
        />
        {/* Provider Rate configuration — public for demo reachability */}
        <Route
          path="/providers"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
                  <div className="text-zinc-500 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Loading provider rates…
                  </div>
                </div>
              }
            >
              <ProviderRates />
            </React.Suspense>
          }
        />
        {/* Spend Protection — runaway-spend firewall, client-side demo */}
        <Route
          path="/spend-protection"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
                  <div className="text-zinc-500 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Loading spend protection…
                  </div>
                </div>
              }
            >
              <SpendProtection />
            </React.Suspense>
          }
        />
        {/* HAVEN — live swarm visualization + real LLM agents + QUBO */}
        <Route
          path="/haven/swarm"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#0B1628] flex items-center justify-center">
                  <div className="text-teal-300 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Initializing HAVEN swarm…
                  </div>
                </div>
              }
            >
              <HavenSwarm />
            </React.Suspense>
          }
        />
        {/* CSH — Grant Prospecting Swarm (public demo) */}
        <Route
          path="/csh/swarm"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#0A1120] flex items-center justify-center">
                  <div className="text-teal-300 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Initializing CSH swarm…
                  </div>
                </div>
              }
            >
              <CshSwarm />
            </React.Suspense>
          }
        />
        {/* Rightsline — Global Licensing Swarm dashboard (isolated tenant) */}
        <Route
          path="/rightsline"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
                  <div className="text-[#00D4AA] text-sm tracking-[0.3em] uppercase animate-pulse">
                    Initializing Rightsline swarm…
                  </div>
                </div>
              }
            >
              <RightslineDashboard />
            </React.Suspense>
          }
        />
        {/* HAVEN — KLR Portfolio Lab (compare, neighborhoods, sensitivity, live data) */}
        <Route
          path="/haven/portfolio-lab"
          element={
            <React.Suspense
              fallback={
                <div className="min-h-screen bg-[#0B1628] flex items-center justify-center">
                  <div className="text-teal-300 text-sm tracking-[0.3em] uppercase animate-pulse">
                    Loading Portfolio Lab…
                  </div>
                </div>
              }
            >
              <PortfolioLab />
            </React.Suspense>
          }
        />
        <Route path="/haven/mandate" element={<ProtectedRoute><React.Suspense fallback={<div className="min-h-screen bg-[#0B1628]" />}><HavenMandate /></React.Suspense></ProtectedRoute>} />
        {/* Rightsline — legacy swarm-demo route now folded into the unified dashboard */}
        <Route path="/swarm/rightsline" element={<Navigate to="/rightsline" replace />} />

        {/* Public redacted aliases for investor demo deep-links (mount directly,
            no redirect, so the URL bar never exposes internal client slugs). */}
        <Route
          path="/grants/swarm"
          element={
            <React.Suspense fallback={<div className="min-h-screen bg-[#0A1120]" />}>
              <CshSwarm />
            </React.Suspense>
          }
        />
        <Route
          path="/licensing"
          element={
            <React.Suspense fallback={<div className="min-h-screen bg-[#0A0E17]" />}>
              <RightslineDashboard />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase A hero reel preview (additive, isolated) */}
        <Route
          path="/investor/hero"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorHero />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase B product sizzle reels (additive, isolated) */}
        <Route
          path="/investor/reel/:product"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorReel />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase B explainer visual scenes (additive, isolated) */}
        <Route
          path="/investor/explainer/:id"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorExplainer />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase C live metrics trust widget (additive, isolated) */}
        <Route
          path="/investor/metrics/preview"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorMetricsPreview />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase D sandboxed demo environments (additive, isolated) */}
        <Route
          path="/investor/sandbox"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorSandbox />
            </React.Suspense>
          }
        />
        <Route
          path="/investor/sandbox/:product"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorSandbox />
            </React.Suspense>
          }
        />

        {/* Investor Portal — Phase E live simulation (additive, isolated) */}
        <Route
          path="/investor/simulate"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorSimulate />
            </React.Suspense>
          }
        />
        <Route
          path="/investor/gallery"
          element={
            <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0A0E17" }} />}>
              <InvestorGallery />
            </React.Suspense>
          }
        />
        {/* SPA catch-all: redirect unknown routes to dashboard if auth'd, else landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

// The classic /dashboard screen is DEACTIVATED as a destination: everything it offered lives
// in the Workspace Dashboard catalog (/app). It still renders in embed mode, which is how the
// catalog iframes its remaining tabs (?tab=…&embed=1).
function ClassicDashboardGate() {
  const params = new URLSearchParams(window.location.search);
  if (params.get("embed") === "1") return <Dashboard />;
  return <Navigate to="/app" replace />;
}

function App() {
  return (
    <ThemeProvider>
      <VoiceAgentProvider>
        <AutomationProvider>
          <ConversationProvider>
            <div className="App">
              <BrowserRouter>
                <AppRouter />
                <SwarmOrchestrator />
                <CookieConsent />
                <Toaster 
                  position="top-right" 
                  richColors 
                  toastOptions={{
                    style: {
                      background: 'var(--qn-card)',
                      border: '1px solid var(--qn-border)',
                      color: 'var(--qn-foreground)',
                    },
                  }}
                />
              </BrowserRouter>
            </div>
          </ConversationProvider>
        </AutomationProvider>
      </VoiceAgentProvider>
    </ThemeProvider>
  );
}

export default App;
